import React from "react";
import {
  Box,
  Stack,
  Container,
  LinearProgress,
  Typography,
} from "@mui/material";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { getScoreColor, getSatisfactionColor, getImportanceColor } from "../utils/helpers";
import { TipsAndUpdates } from "@mui/icons-material";

const SubSection = ({ subSection }) => {
  return (
    <Box sx={{ mb: 6, px: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          sx={{
            color: "#4B5563",
            flex: 1,
            fontSize: "22px",
            fontWeight: "bold",
            textWrap: "wrap",
            wordBreak: "break-word",
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          {subSection.title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 1, sm: 2 },
            width: { xs: "100%", sm: "auto" },
            px: { xs: 2, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: `1px solid ${getSatisfactionColor(
                subSection.passed ? "Excellent" : "Failed"
              )}`,
              bgcolor: `${getSatisfactionColor(
                subSection.passed ? "Excellent" : "Failed"
              )}20`,
              px: { xs: 0.5, sm: 1 },
              py: { xs: 0, sm: 0.5 },
              borderRadius: "5px",
            }}
          >
            {subSection.passed ? (
              <ThumbsUp size={20} color={getSatisfactionColor("Excellent")} />
            ) : (
              <ThumbsDown size={20} color={getSatisfactionColor("Failed")} />
            )}
            <Typography
              variant="caption"
              color={getSatisfactionColor(
                subSection.passed ? "Excellent" : "Failed"
              )}
            >
              {subSection.passed ? "Yes" : "No"}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: `${getImportanceColor(subSection.impact)}`, fontWeight: "bold" }}
          >
            {subSection.impact}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {subSection.description}
      </Typography>

      {!subSection.passed && (
        <Box>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontSize: "19px", fontWeight: "550" }}
          >
            <TipsAndUpdates sx={{ mr: "5px", fontSize:"15px", color: "rgb(249, 181, 86)" }} />Recommendations:
          </Typography>
          <Stack spacing={2}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ pl: { xs: 0, sm: 3 }, position: "relative" }}
            >
              • {subSection.recommended_action}
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const PerfoSec = ({ data }) => {
  return (
    <Box sx={{ pt: 6 }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 4,
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: "bold" }}
          >
            {data.category_name}
          </Typography>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: getScoreColor(data.category_score),
              fontSize: "28px",
            }}
          >
            {data.category_score}%
          </Typography>
        </Box>

        <Box sx={{ mb: 6 }}>
          <LinearProgress
            variant="determinate"
            value={data.category_score}
            sx={{
              height: 8,
              borderRadius: 4,
              "& .MuiLinearProgress-bar": {
                bgcolor: getScoreColor(data.category_score),
                borderRadius: 4,
              },
            }}
          />
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {data.description}
        </Typography>

        {data.guidelines.map((subSection) => (
          <>
            <SubSection subSection={subSection} />
            <Box sx={{ my: 4, borderBottom: "1px solid #e0e0e0" }} />
          </>
        ))}
      </Container>
    </Box>
  );
};

export default PerfoSec;
